import React, { useState, useEffect, useCallback } from 'react';
import { FINANCIAL_API, authFetch } from '../constants';
import { useDrillDown, DrillDownRow } from '../components/DrillDown';
import TabBar from '../components/ui/TabBar';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';
import { useGlobalEntity } from '../hooks/useGlobalEntity';

const CONSOLIDATED = '__CONSOLIDATED__';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fmtSigned = (n: number) =>
  n < 0 ? `(${fmt(Math.abs(n))})` : fmt(n);

const fmtImpact = (impact: number) =>
  Math.abs(impact) < 0.005 ? '\u2014' : (impact > 0 ? `+${fmt(impact)}` : `(${fmt(Math.abs(impact))})`);

type ViewMode = 'before' | 'conversion' | 'reporting' | 'adjustments' | 'after' | 'after_all' | 'comparison';
type LineItem = { account_code: string; account_name: string; amount: number; display_order: number };
type Section = { label: string; accounts: LineItem[]; subtotal: number };

export default function IncomeStatement() {
  const { entity: globalEntity } = useGlobalEntity();
  const [entities, setEntities] = useState<any[]>([]);
  const [entity, setEntity] = useState('');
  const [periods, setPeriods] = useState<string[]>([]);
  const [period, setPeriod] = useState('');
  const [view, setView] = useState<ViewMode>('after');
  const [data, setData] = useState<any>(null);
  const [beforeData, setBeforeData] = useState<any>(null);
  const [afterData, setAfterData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showUSD, setShowUSD] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [pendingInfo, setPendingInfo] = useState<any>(null);

  const drill = useDrillDown();

  const entityCurrency = entities.find(e => e.entity_code === entity)?.functional_currency || '';
  const isUSD = entity === CONSOLIDATED || entityCurrency === 'USD';
  const isConsolidated = entity === CONSOLIDATED;
  const isComparison = view === 'comparison';

  useEffect(() => {
    authFetch(`${FINANCIAL_API}/entities`)
      .then(r => r.json())
      .then(rows => {
        setEntities(rows);
        if (rows.length > 0 && !entity) setEntity(globalEntity || rows[0].entity_code);
      });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (globalEntity && globalEntity !== entity) setEntity(globalEntity);
  }, [globalEntity]); // eslint-disable-line

  useEffect(() => {
    if (!entity) return;
    const url = isConsolidated
      ? `${FINANCIAL_API}/consolidated/available-periods`
      : `${FINANCIAL_API}/available-periods?entity_code=${entity}`;
    authFetch(url)
      .then(r => r.json())
      .then(d => {
        setPeriods(d.periods || []);
        if (d.periods?.length) setPeriod(d.periods[d.periods.length - 1]);
      });
    setShowUSD(false);
  }, [entity, isConsolidated]);

  const buildUrl = useCallback((v: string) => {
    const currSuffix = showUSD && !isUSD ? '&currency=USD' : '';
    return isConsolidated
      ? `${FINANCIAL_API}/consolidated/income-statement?period=${period}&view=${v}${currSuffix}`
      : `${FINANCIAL_API}/income-statement?entity_code=${entity}&period=${period}&view=${v}${currSuffix}`;
  }, [entity, period, showUSD, isUSD, isConsolidated]);

  const loadData = useCallback(() => {
    if (!entity || !period) return;
    setLoading(true);
    drill.resetDrill();

    if (isComparison) {
      Promise.all([
        authFetch(buildUrl('before')).then(r => r.json()),
        authFetch(buildUrl('after')).then(r => r.json()),
      ]).then(([b, a]) => {
        setBeforeData(b);
        setAfterData(a);
        setData(a);
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      authFetch(buildUrl(view))
        .then(r => r.json())
        .then(d => { setData(d); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [entity, period, view, showUSD, isUSD, isConsolidated, isComparison, buildUrl]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!entity) return;
    const url = isConsolidated
      ? `${FINANCIAL_API}/consolidated/pending-adjustments`
      : `${FINANCIAL_API}/pending-adjustments?entity_code=${entity}`;
    authFetch(url).then(r => r.json()).then(setPendingInfo).catch(() => setPendingInfo(null));
  }, [entity, isConsolidated]);

  const handleExport = async (format: 'pdf' | 'excel') => {
    setExporting(format);
    try {
      const body = {
        statement: 'income-statement',
        entity_code: isConsolidated ? 'CONSOLIDATED' : entity,
        period, view,
        currency: showUSD && !isUSD ? 'USD' : undefined,
      };
      const resp = await authFetch(`${FINANCIAL_API}/export/${format}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `income-statement-${entity}-${period}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Export failed:', err); }
    setExporting(null);
  };

  const needsConversion = (() => {
    if (isConsolidated) return true;
    const ent = entities.find(e => e.entity_code === entity);
    return ent && ent.local_framework && ent.local_framework !== ent.reporting_framework;
  })();

  const allViewButtons: { key: ViewMode; label: string }[] = [
    { key: 'before', label: 'Local' },
    { key: 'conversion', label: 'Conversion' },
    { key: 'reporting', label: 'Reporting' },
    { key: 'adjustments', label: 'Adjustments' },
    { key: 'after', label: 'After' },
    { key: 'after_all', label: 'After (All)' },
    { key: 'comparison', label: 'Comparison' },
  ];
  const viewButtons = allViewButtons.filter(v =>
    needsConversion || !['conversion', 'reporting'].includes(v.key)
  );

  const impactColor = (impact: number, isExpense: boolean) => {
    if (Math.abs(impact) < 0.005) return 'var(--text-hint)';
    const isGood = isExpense ? impact < 0 : impact > 0;
    return isGood ? 'var(--accent-good)' : 'var(--accent-critical)';
  };

  const getAcctAmt = (sData: any, sectionKey: string, code: string): number => {
    if (!sData?.sections) return 0;
    const section = sData.sections[sectionKey];
    if (!section?.accounts) return 0;
    const acct = section.accounts.find((a: LineItem) => a.account_code === code);
    return acct?.amount || 0;
  };

  // Normal view renderers
  const renderItems = (section: Section) => (
    <>
      <tr style={{ background: 'rgba(0,0,0,0.02)' }}>
        <td colSpan={3} style={{
          padding: '10px 14px 4px',
          fontSize: 11, fontWeight: 600,
          color: 'var(--text-hint)',
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>{section.label}</td>
      </tr>
      {section.accounts.map(a => (
        <React.Fragment key={a.account_code}>
          <tr
            onClick={() => a.account_code && drill.toggleAccount(a.account_code, entity, period, view, isConsolidated)}
            style={{
              borderBottom: '1px solid var(--border-subtle)',
              cursor: a.account_code ? 'pointer' : 'default',
              background: drill.expandedAcct === a.account_code ? 'var(--accent-subtle)' : 'transparent',
            }}
            onMouseEnter={e => { if (a.account_code && drill.expandedAcct !== a.account_code) (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { if (a.account_code && drill.expandedAcct !== a.account_code) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <td style={tdIndent} className="mono">
              {a.account_code && (
                <span style={{ color: 'var(--accent)', marginRight: 6, fontSize: 10 }}>
                  {drill.expandedAcct === a.account_code ? '\u25BC' : '\u25B6'}
                </span>
              )}
              {a.account_code}
            </td>
            <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>{a.account_name}</td>
            <td style={tdRight}>{fmtSigned(a.amount)}</td>
          </tr>

          {drill.expandedAcct === a.account_code && (
            <DrillDownRow
              colSpan={3}
              isLoading={drill.acctLoading}
              detail={drill.acctDetail}
              isConsolidated={isConsolidated}
              expandedJE={drill.expandedJE}
              jeDetail={drill.jeDetail}
              jeLoading={drill.jeLoading}
              toggleJE={drill.toggleJE}
            />
          )}
        </React.Fragment>
      ))}
      <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        <td style={tdStyle}></td>
        <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary)' }}>
          Total {section.label}
        </td>
        <td style={{ ...tdRight, fontWeight: 600, borderTop: '1px solid var(--text-hint)' }}>
          {fmtSigned(section.subtotal)}
        </td>
      </tr>
    </>
  );

  const renderCalcLine = (label: string, amount: number, highlight = false) => (
    <tr style={{
      background: highlight ? 'var(--accent-subtle)' : 'rgba(0,0,0,0.02)',
      borderBottom: '1px solid var(--border-subtle)',
    }}>
      <td style={tdStyle}></td>
      <td style={{
        ...tdStyle,
        fontWeight: 700,
        fontSize: highlight ? 14 : 13,
        color: 'var(--text-primary)',
      }}>{label}</td>
      <td style={{
        ...tdRight,
        fontWeight: 700,
        fontSize: highlight ? 14 : 13,
        color: amount < 0 ? 'var(--accent-critical)' : amount > 0 ? 'var(--accent-good)' : 'var(--text-primary)',
        borderTop: highlight ? '2px solid var(--border-subtle)' : 'none',
      }}>{fmtSigned(amount)}</td>
    </tr>
  );

  // Comparison view renderers
  const renderCompSection = (sectionKey: string, isExpense: boolean) => {
    const bSec: Section | undefined = beforeData?.sections?.[sectionKey];
    const aSec: Section | undefined = afterData?.sections?.[sectionKey];
    if (!bSec && !aSec) return null;
    const allCodes = new Set([
      ...(bSec?.accounts || []).map(a => a.account_code),
      ...(aSec?.accounts || []).map(a => a.account_code),
    ]);
    const bSub = bSec?.subtotal || 0;
    const aSub = aSec?.subtotal || 0;
    const subImpact = aSub - bSub;

    return (
      <>
        <tr style={{ background: 'rgba(0,0,0,0.02)' }}>
          <td colSpan={5} style={{
            padding: '10px 14px 4px',
            fontSize: 11, fontWeight: 600,
            color: 'var(--text-hint)',
            textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>{bSec?.label || aSec?.label}</td>
        </tr>
        {Array.from(allCodes).sort().map(code => {
          const bAmt = getAcctAmt(beforeData, sectionKey, code);
          const aAmt = getAcctAmt(afterData, sectionKey, code);
          const impact = aAmt - bAmt;
          const name = (aSec?.accounts || bSec?.accounts || []).find(a => a.account_code === code)?.account_name || '';
          return (
            <tr key={code} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <td style={tdIndent} className="mono">{code}</td>
              <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>{name}</td>
              <td style={tdRight}>{fmtSigned(bAmt)}</td>
              <td style={tdRight}>{fmtSigned(aAmt)}</td>
              <td style={{ ...tdRight, fontWeight: 600, color: impactColor(impact, isExpense) }}>
                {fmtImpact(impact)}
              </td>
            </tr>
          );
        })}
        <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <td style={tdStyle}></td>
          <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary)' }}>
            Total {bSec?.label || aSec?.label}
          </td>
          <td style={{ ...tdRight, fontWeight: 600, borderTop: '1px solid var(--text-hint)' }}>{fmtSigned(bSub)}</td>
          <td style={{ ...tdRight, fontWeight: 600, borderTop: '1px solid var(--text-hint)' }}>{fmtSigned(aSub)}</td>
          <td style={{ ...tdRight, fontWeight: 600, borderTop: '1px solid var(--text-hint)', color: impactColor(subImpact, isExpense) }}>
            {fmtImpact(subImpact)}
          </td>
        </tr>
      </>
    );
  };

  const renderCompCalcLine = (label: string, beforeVal: number, afterVal: number, highlight = false) => {
    const impact = afterVal - beforeVal;
    const isGood = impact > 0;
    const color = Math.abs(impact) < 0.005 ? 'var(--text-hint)' : (isGood ? 'var(--accent-good)' : 'var(--accent-critical)');
    return (
      <tr style={{
        background: highlight ? 'rgba(0,0,0,0.03)' : 'rgba(0,0,0,0.02)',
        borderBottom: '1px solid var(--border-subtle)',
      }}>
        <td style={tdStyle}></td>
        <td style={{
          ...tdStyle, fontWeight: 700,
          fontSize: highlight ? 14 : 13,
          color: 'var(--text-primary)',
        }}>{label}</td>
        <td style={{ ...tdRight, fontWeight: 700, fontSize: highlight ? 14 : 13 }}>{fmtSigned(beforeVal)}</td>
        <td style={{ ...tdRight, fontWeight: 700, fontSize: highlight ? 14 : 13 }}>{fmtSigned(afterVal)}</td>
        <td style={{
          ...tdRight, fontWeight: 700, fontSize: highlight ? 14 : 13,
          color,
          borderTop: highlight ? '2px solid var(--border-subtle)' : 'none',
        }}>{fmtImpact(impact)}</td>
      </tr>
    );
  };

  const bs = beforeData?.sections;
  const as_ = afterData?.sections;
  const s = data?.sections;

  return (
    <div style={{ maxWidth: isComparison ? 1100 : 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy-900)', margin: 0 }}>
          Income Statement
        </h2>
        {data && (
          <p style={{ fontSize: 13, color: 'var(--text-hint)', margin: '4px 0 0' }}>
            {data.entity_name} &middot; FY {data.fiscal_year_start} to {data.period_end} &middot; Currency: {data.currency}
            {isComparison && ' \u00b7 Comparison: Before vs After'}
          </p>
        )}
      </div>

      <FilterBar filters={[
        {
          key: 'entity', label: 'Entity', type: 'select', value: entity,
          onChange: v => setEntity(v),
          options: [
            ...entities.map(e => ({ value: e.entity_code, label: `${e.entity_name} (${e.functional_currency})` })),
            { value: CONSOLIDATED, label: 'Consolidated (USD)' },
          ],
          placeholder: 'Select Entity',
        },
        {
          key: 'period', label: 'Period', type: 'select', value: period,
          onChange: v => setPeriod(v),
          options: periods.map(p => ({ value: p, label: p })),
          placeholder: 'Select Period',
        },
      ]} />

      <TabBar
        tabs={viewButtons.map(v => ({ key: v.key, label: v.label }))}
        active={view}
        onChange={k => setView(k as ViewMode)}
      />

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {!isUSD && (
          <ActionButton
            label={showUSD ? 'USD' : entityCurrency}
            onClick={() => setShowUSD(!showUSD)}
            variant={showUSD ? 'primary' : 'secondary'}
            size="sm"
          />
        )}
        <ActionButton label={exporting === 'pdf' ? '...' : 'PDF'} onClick={() => handleExport('pdf')} variant="ghost" size="sm" disabled={!!exporting || !data} />
        <ActionButton label={exporting === 'excel' ? '...' : 'Excel'} onClick={() => handleExport('excel')} variant="ghost" size="sm" disabled={!!exporting || !data} />
        {loading && <span style={{ fontSize: 12, color: 'var(--text-hint)' }}>Loading...</span>}
      </div>

      {/* Provisional warning banner */}
      {view === 'after_all' && (
        <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-border)', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>!</span>
          <span style={{ fontSize: 12, color: 'var(--warning)' }}>This view includes <strong>PROVISIONAL</strong> adjustments not yet confirmed by a supervisor.</span>
        </div>
      )}

      {/* Pending adjustments card */}
      {pendingInfo && (pendingInfo.provisional_jes?.count > 0 || pendingInfo.pending_actions?.count > 0 || pendingInfo.unresolved_exceptions?.total > 0) && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', gap: 24, alignItems: 'center', fontSize: 12 }}>
          <span style={{ color: 'var(--text-hint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending</span>
          {pendingInfo.provisional_jes?.count > 0 && (
            <span style={{ color: 'var(--warning)' }}>{pendingInfo.provisional_jes.count} provisional JE{pendingInfo.provisional_jes.count !== 1 ? 's' : ''}</span>
          )}
          {pendingInfo.pending_actions?.count > 0 && (
            <span style={{ color: 'var(--navy-700)' }}>{pendingInfo.pending_actions.count} action{pendingInfo.pending_actions.count !== 1 ? 's' : ''} in review</span>
          )}
          {pendingInfo.unresolved_exceptions?.total > 0 && (
            <span style={{ color: 'var(--text-hint)' }}>{pendingInfo.unresolved_exceptions.total} unresolved exception{pendingInfo.unresolved_exceptions.total !== 1 ? 's' : ''}</span>
          )}
        </div>
      )}

      {/* === COMPARISON VIEW === */}
      {isComparison && bs && as_ && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 100 }}>Code</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Account</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 140 }}>Before</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 140 }}>After</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 140 }}>Impact</th>
              </tr>
            </thead>
            <tbody>
              {renderCompSection('revenue', false)}
              {(bs.cogs?.accounts?.length > 0 || as_.cogs?.accounts?.length > 0) && renderCompSection('cogs', true)}
              {renderCompCalcLine('Gross Profit', bs.gross_profit || 0, as_.gross_profit || 0)}

              <tr><td colSpan={5} style={{ height: 8 }}></td></tr>

              {renderCompSection('opex', true)}
              {renderCompCalcLine('Operating Income', bs.operating_income || 0, as_.operating_income || 0)}

              {(bs.other?.accounts?.length > 0 || as_.other?.accounts?.length > 0) && (
                <>
                  <tr><td colSpan={5} style={{ height: 8 }}></td></tr>
                  {renderCompSection('other', true)}
                </>
              )}

              <tr><td colSpan={5} style={{ height: 8 }}></td></tr>

              {renderCompCalcLine('Net Income', bs.net_income || 0, as_.net_income || 0, true)}
            </tbody>
          </table>
        </div>
      )}

      {/* === NORMAL VIEW === */}
      {!isComparison && data && s && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 100 }}>Code</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Account</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 160 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {renderItems(s.revenue)}
              {s.cogs.accounts.length > 0 && renderItems(s.cogs)}
              {renderCalcLine('Gross Profit', s.gross_profit)}

              <tr><td colSpan={3} style={{ height: 8 }}></td></tr>

              {renderItems(s.opex)}
              {renderCalcLine('Operating Income', s.operating_income)}

              {s.other.accounts.length > 0 && (
                <>
                  <tr><td colSpan={3} style={{ height: 8 }}></td></tr>
                  {renderItems(s.other)}
                </>
              )}

              <tr><td colSpan={3} style={{ height: 8 }}></td></tr>

              {renderCalcLine('Net Income', s.net_income, true)}
            </tbody>
          </table>
        </div>
      )}

      {/* Net Income banner (normal view) */}
      {!isComparison && data && s && (
        <div style={{
          marginTop: 16, padding: '10px 16px', borderRadius: 8,
          background: s.net_income >= 0 ? 'var(--positive-bg)' : 'var(--negative-bg)',
          border: `1px solid ${s.net_income >= 0 ? 'var(--accent-good)' : 'var(--accent-critical)'}`,
          fontSize: 13, fontWeight: 600,
          color: s.net_income >= 0 ? 'var(--accent-good)' : 'var(--accent-critical)',
        }}>
          Net Income: {fmtSigned(s.net_income)} {data.currency}
        </div>
      )}

      {/* Comparison summary */}
      {isComparison && bs && as_ && (
        <div style={{
          marginTop: 16, padding: '10px 16px', borderRadius: 8,
          background: 'var(--accent-subtle)',
          border: '1px solid var(--accent-border)',
          fontSize: 13, fontWeight: 600, color: 'var(--accent)',
        }}>
          Net Income Impact: {fmtImpact((as_.net_income || 0) - (bs.net_income || 0))} {data?.currency}
          {' \u00b7 '}
          Revenue Impact: {fmtImpact((as_.revenue?.subtotal || 0) - (bs.revenue?.subtotal || 0))}
          {' \u00b7 '}
          Expense Impact: {fmtImpact(((as_.cogs?.subtotal || 0) + (as_.opex?.subtotal || 0)) - ((bs.cogs?.subtotal || 0) + (bs.opex?.subtotal || 0)))}
        </div>
      )}

      {data && !isComparison && !data.sections && !loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 14 }}>
          No data for this entity/period/view combination.
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px', textAlign: 'left',
  fontSize: 11, fontWeight: 600, color: 'var(--text-hint)',
  textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: '2px solid var(--border-subtle)',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)',
};

const tdIndent: React.CSSProperties = {
  ...tdStyle, paddingLeft: 28, fontVariantNumeric: 'tabular-nums', fontSize: 12, color: 'var(--text-hint)',
};

const tdRight: React.CSSProperties = {
  ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
};
